import UmlShapeWidgetBase from './UmlShapeWidgetBase';
import umlCanvasEngine from '../../Core/UmlCanvasEngine';
import { formatSeqParams } from '../../Core/CsSeqDia/CsSeqParamsFormatter';
import './UmlSeqMessageWidget.css';

// Pure painter for UmlSeqMessageShape — horizontal arrow between two
// lifelines at a fixed row-y. x/y/w/h are kept in sync by
// CsSeqDiaRelationHandler whenever either endpoint participant moves — plain
// shape.x/y/w/h/fromX/toX rendering, no live getShapeById lookup needed here.
// shape.label is its OWN UmlCaption (x/y offset relative to this shape's own
// x/y, same pattern as a UmlConnector's label) — independently draggable via
// handleLabelMouseDown, never baked into the arrow's own position/rendering.
// Dashed line for boundary steps AND for return arrows (isReturn). No resize
// handles on the arrow itself — geometry is computed once at generation time
// and kept in sync afterwards, not user-adjustable directly.
class UmlSeqMessageWidget extends UmlShapeWidgetBase {
    getDisabledHandles() {
        return ['nw', 'ne', 'sw', 'se', 'n', 's', 'e', 'w'];
    }

    // Only drags VERTICALLY — x stays locked to whatever its two participants
    // dictate (see UmlCanvasEngine.updateShapePosition), so the arrow can be
    // repositioned up/down in the sequence but never pulled loose sideways
    // from the activation bars it connects.

    // Starts a drag gesture for the label ALONE — label.x/y are an offset
    // relative to this shape's own x/y (not the arrow's midpoint, unlike a
    // connector — a message's x/w already changes independently via
    // CsSeqDiaRelationHandler, so anchoring to the shape's stable x/y corner
    // avoids the offset silently drifting as the arrow's width changes).
    handleLabelMouseDown = (e) => {
        if (e.button !== 0) return;
        e.preventDefault();
        e.stopPropagation();

        const shape = this.props.shape;
        const startWorld = this.props.toWorldPoint(e.clientX, e.clientY);
        const dragOffset = { x: startWorld.x - (shape.x + shape.label.x), y: startWorld.y - (shape.y + shape.label.y) };

        const onMouseMove = (ev) => {
            const worldPoint = this.props.toWorldPoint(ev.clientX, ev.clientY);
            const newLabelX = worldPoint.x - dragOffset.x - shape.x;
            const newLabelY = worldPoint.y - dragOffset.y - shape.y;
            this.props.onSeqMessageLabelMoved?.(shape.id, newLabelX, newLabelY);
        };
        const onMouseUp = () => {
            document.body.style.userSelect = '';
            document.body.style.cursor = '';
            window.removeEventListener('mousemove', onMouseMove, true);
            window.removeEventListener('mouseup', onMouseUp, true);
        };

        document.body.style.userSelect = 'none';
        document.body.style.cursor = 'grabbing';
        window.addEventListener('mousemove', onMouseMove, true);
        window.addEventListener('mouseup', onMouseUp, true);
    };

    render() {
        const shape = this.props.shape;
        // Shrink both ends inward by half an activation bar's width (10/2=5,
        // see UmlSeqActivationShape's ACTIVATION_WIDTH in CsSeqCanvasLayoutBuilder.js
        // — kept in sync manually, it's a small fixed visual constant) so the
        // arrow stops at the bar's EDGE instead of piercing through its
        // center. A participant without an actual bar here just gets a few
        // px of harmless extra gap before the lifeline.
        const ACTIVATION_HALF_WIDTH = 5;
        const rightward = shape.toX >= shape.fromX;
        const arrowX = rightward ? shape.w - ACTIVATION_HALF_WIDTH : ACTIVATION_HALF_WIDTH;
        const tailX = rightward ? ACTIVATION_HALF_WIDTH : shape.w - ACTIVATION_HALF_WIDTH;
        const midY = shape.h - 4;
        const arrowPoints = rightward
            ? `${arrowX - 7},${midY - 4} ${arrowX},${midY} ${arrowX - 7},${midY + 4}`
            : `${arrowX + 7},${midY - 4} ${arrowX},${midY} ${arrowX + 7},${midY + 4}`;
        const dashed = shape.boundary || shape.isReturn;
        const lineClass = dashed ? 'uml-seq-message-widget-line-boundary' : 'uml-seq-message-widget-line-solid';

        return (
            <div style={{ display: 'contents' }}>
                <div
                    className={`uml-seq-message-widget${this.props.isSelected ? ' uml-selected' : ''}`}
                    data-shape-id={shape.id}
                    style={{ left: shape.x, top: shape.y, width: shape.w, height: shape.h, zIndex: this.props.stackIndex ?? shape.zIndex }}
                    onMouseDown={this.handleDragMouseDown}
                >
                    <svg className="uml-seq-message-widget-svg" width={shape.w} height={shape.h}>
                        <line x1={tailX} y1={midY} x2={arrowX} y2={midY} className={lineClass} />
                        <polygon points={arrowPoints} className="uml-seq-message-widget-arrowhead" />
                    </svg>
                </div>
                {shape.label?.text && (
                    <div
                        className="uml-seq-message-widget-label"
                        style={{ left: shape.x + shape.label.x, top: shape.y + shape.label.y, zIndex: (this.props.stackIndex ?? shape.zIndex) + 1 }}
                        onMouseDown={this.handleLabelMouseDown}
                    >
                        {shape.label.text}
                        {!shape.isReturn && formatSeqParams(shape.params, umlCanvasEngine.viewConfig.seqParamsMode) && (
                            <span className="uml-seq-message-widget-label-params"> {formatSeqParams(shape.params, umlCanvasEngine.viewConfig.seqParamsMode)}</span>
                        )}
                    </div>
                )}
            </div>
        );
    }
}

export default UmlSeqMessageWidget;
